import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, KeyRound, Loader2, Mail, ShieldCheck, Smartphone } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { useToast } from '@/hooks'
import { authService } from '@/services'
import { getErrorMessage } from '@/utils/error'
import type { TwoFactorMethod, TwoFactorSetupResponse, User } from '@/types'

type Flow = null | 'totp' | 'email' | 'disable' | 'backup'

interface Props {
  user: User
}

function OtpField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div className="flex justify-center py-2">
      <InputOTP maxLength={6} value={value} onChange={onChange} disabled={disabled}>
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  )
}

/**
 * Seção de 2FA no perfil: ativa/desativa, escolhe método (app TOTP ou e-mail),
 * mostra QR + backup codes. Contas Microsoft não usam (MFA é do tenant).
 */
export function TwoFactorSettings({ user }: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isOAuth = user.authProvider === 'microsoft'

  const [flow, setFlow] = useState<Flow>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])

  const { data: status, isLoading } = useQuery({
    queryKey: ['2fa', 'status'],
    queryFn: authService.getTwoFactorStatus,
    enabled: !isOAuth,
  })

  function closeDialog() {
    setFlow(null)
    setCode('')
    setPassword('')
    setSetupData(null)
    setBackupCodes([])
  }

  function refreshStatus() {
    queryClient.invalidateQueries({ queryKey: ['2fa', 'status'] })
  }

  function onError(error: unknown, fallback: string) {
    toast({ title: 'Erro', description: getErrorMessage(error, fallback), variant: 'destructive' })
  }

  const setupMutation = useMutation({
    mutationFn: (method: TwoFactorMethod) => authService.setupTwoFactor(method),
    onSuccess: (data) => {
      setSetupData(data)
      setCode('')
      setFlow(data.method)
      if (data.method === 'email') {
        toast({ title: 'Código enviado', description: 'Confira seu e-mail.' })
      }
    },
    onError: (e) => onError(e, 'Não foi possível iniciar o cadastro'),
  })

  const enableMutation = useMutation({
    mutationFn: ({ method, code }: { method: TwoFactorMethod; code: string }) =>
      authService.enableTwoFactor(method, code),
    onSuccess: (res) => {
      refreshStatus()
      if (res.backupCodes?.length) {
        setBackupCodes(res.backupCodes)
        setFlow('backup')
        setCode('')
      } else {
        toast({ title: '2FA ativado', description: 'Sua conta está mais protegida.' })
        closeDialog()
      }
    },
    onError: (e) => onError(e, 'Código inválido'),
  })

  const disableMutation = useMutation({
    mutationFn: (password: string) => authService.disableTwoFactor(password),
    onSuccess: () => {
      refreshStatus()
      toast({ title: '2FA desativado' })
      closeDialog()
    },
    onError: (e) => onError(e, 'Senha incorreta'),
  })

  const regenerateMutation = useMutation({
    mutationFn: () => authService.regenerateBackupCodes(),
    onSuccess: (res) => {
      setBackupCodes(res.backupCodes)
      setFlow('backup')
    },
    onError: (e) => onError(e, 'Não foi possível gerar novos códigos'),
  })

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    toast({ title: 'Códigos copiados' })
  }

  if (isOAuth) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Autenticação de dois fatores
          </CardTitle>
          <CardDescription>
            Sua conta entra via Microsoft — a verificação em duas etapas é gerenciada pela sua
            organização.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Autenticação de dois fatores
            {status?.enabled && (
              <Badge className="ml-1 bg-primary/15 text-primary hover:bg-primary/15">Ativo</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Adicione uma segunda camada de segurança ao entrar na sua conta.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : status?.enabled ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Método ativo:{' '}
                <span className="text-foreground font-medium">
                  {status.method === 'totp' ? 'App autenticador' : 'Código por e-mail'}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {status.method === 'totp' && (
                  <Button
                    variant="outline"
                    onClick={() => regenerateMutation.mutate()}
                    disabled={regenerateMutation.isPending}
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Novos códigos de backup
                  </Button>
                )}
                <Button variant="destructive" onClick={() => setFlow('disable')}>
                  Desativar 2FA
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setupMutation.mutate('totp')}
                disabled={setupMutation.isPending}
                className="flex items-start gap-3 rounded-lg border border-border/60 p-4 text-left hover:border-primary/60 transition-colors disabled:opacity-60"
              >
                <Smartphone className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <p className="font-medium text-sm">App autenticador</p>
                  <p className="text-xs text-muted-foreground">
                    Google Authenticator, Authy etc. Mais seguro.
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setupMutation.mutate('email')}
                disabled={setupMutation.isPending}
                className="flex items-start gap-3 rounded-lg border border-border/60 p-4 text-left hover:border-primary/60 transition-colors disabled:opacity-60"
              >
                <Mail className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <p className="font-medium text-sm">Código por e-mail</p>
                  <p className="text-xs text-muted-foreground">Enviamos um código a cada login.</p>
                </div>
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={flow !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md">
          {/* Setup TOTP */}
          {flow === 'totp' && setupData?.qrDataUrl && (
            <>
              <DialogHeader>
                <DialogTitle>Configurar app autenticador</DialogTitle>
                <DialogDescription>
                  Escaneie o QR code com seu app e digite o código gerado.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center gap-3">
                <img
                  src={setupData.qrDataUrl}
                  alt="QR code 2FA"
                  className="w-44 h-44 rounded-lg bg-white p-2"
                />
                {setupData.secret && (
                  <p className="text-xs text-muted-foreground break-all text-center">
                    Ou insira manualmente: <span className="font-mono">{setupData.secret}</span>
                  </p>
                )}
              </div>
              <OtpField value={code} onChange={setCode} disabled={enableMutation.isPending} />
              <DialogFooter>
                <Button
                  className="w-full"
                  disabled={code.length < 6 || enableMutation.isPending}
                  onClick={() => enableMutation.mutate({ method: 'totp', code })}
                >
                  {enableMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Ativar
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Setup email */}
          {flow === 'email' && (
            <>
              <DialogHeader>
                <DialogTitle>Confirmar e-mail</DialogTitle>
                <DialogDescription>
                  Digite o código de 6 dígitos enviado para {user.email}.
                </DialogDescription>
              </DialogHeader>
              <OtpField value={code} onChange={setCode} disabled={enableMutation.isPending} />
              <DialogFooter>
                <Button
                  className="w-full"
                  disabled={code.length < 6 || enableMutation.isPending}
                  onClick={() => enableMutation.mutate({ method: 'email', code })}
                >
                  {enableMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Ativar
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Disable */}
          {flow === 'disable' && (
            <>
              <DialogHeader>
                <DialogTitle>Desativar 2FA</DialogTitle>
                <DialogDescription>
                  Confirme sua senha para desativar a verificação em duas etapas.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="disable-pw">Senha</Label>
                <Input
                  id="disable-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={!password || disableMutation.isPending}
                  onClick={() => disableMutation.mutate(password)}
                >
                  {disableMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Desativar
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Backup codes */}
          {flow === 'backup' && (
            <>
              <DialogHeader>
                <DialogTitle>Guarde seus códigos de backup</DialogTitle>
                <DialogDescription>
                  Cada código funciona uma vez. Use-os se perder acesso ao app. Eles não serão
                  exibidos de novo.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-4 font-mono text-sm">
                {backupCodes.map((c) => (
                  <span key={c} className="text-center tracking-wider">
                    {c}
                  </span>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={copyBackupCodes}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
                <Button onClick={closeDialog}>Concluí, guardei</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
